/* eslint-disable */
import React from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { Grid, AutoSizer, defaultCellRangeRenderer } from 'react-virtualized';

import moment from 'moment';
import interact from 'interactjs';
import _ from 'lodash';

import { DraggableCore } from 'react-draggable';
import { pixToInt, intToPix, sumStyle } from 'libs/timeline/utils/commonUtils';
import {
  rowItemsRenderer,
  rowLayerRenderer,
  getNearestRowNumber,
  getNearestRowObject,
  getMaxOverlappingItems,
  getTrueBottom,
  getVerticalMarginBorder,
  getRowObjectRowNumber
} from 'libs/timeline/utils/itemUtils';
import {
  timeSnap, getTimeAtPixel, getPixelAtTime, getSnapPixelFromDelta, pixelsPerMinute
} from 'libs/timeline/utils/timeUtils';
import Timebar from 'libs/timeline/components/timebar';
import SelectBox from 'libs/timeline/components/selector';
import { DefaultGroupRenderer, DefaultItemRenderer } from 'libs/timeline/components/renderers';
import TimelineBody from 'libs/timeline/components/body';
import Marker from 'libs/timeline/components/marker';
import { perfCount } from 'libs/perf/rosterRenderPerf';

// startsWith polyfill for IE11 support
import 'core-js/fn/string/starts-with';

/**
 * Timeline class
 * @reactProps {!number} items - this is prop1
 * @reactProps {string} prop2 - this is prop2
 */
export default class Timeline extends React.Component {
  /**
   * @type {object}
   */
  static TIMELINE_MODES = Object.freeze({
    SELECT: 1,
    DRAG: 2,
    RESIZE: 4
  });

  static propTypes = {
    items: PropTypes.arrayOf(PropTypes.object).isRequired,
    groups: PropTypes.arrayOf(PropTypes.object).isRequired,
    groupOffset: PropTypes.number.isRequired,
    rowLayers: PropTypes.arrayOf(
      PropTypes.shape({
        start: PropTypes.object.isRequired,
        end: PropTypes.object.isRequired,
        rowNumber: PropTypes.number.isRequired,
        style: PropTypes.object.isRequired
      })
    ),
    selectedItems: PropTypes.arrayOf(PropTypes.number),
    startDate: PropTypes.object.isRequired,
    endDate: PropTypes.object.isRequired,
    snapMinutes: PropTypes.number,
    showCursorTime: PropTypes.bool,
    cursorTimeFormat: PropTypes.string,
    componentId: PropTypes.string, // A unique key to identify the component. Only needed when 2 grids are mounted
    itemHeight: PropTypes.number,
    timelineMode: PropTypes.number,
    timebarFormat: PropTypes.object,
    onItemClick: PropTypes.func,
    onItemDoubleClick: PropTypes.func,
    onItemContext: PropTypes.func,
    onInteraction: PropTypes.func.isRequired,
    onRowClick: PropTypes.func,
    onRowContext: PropTypes.func,
    onRowDoubleClick: PropTypes.func,
    onRowMouseMove: PropTypes.func,
    onRowContextClick: PropTypes.func,
    onItemHover: PropTypes.func,
    onItemLeave: PropTypes.func,
    itemRenderer: PropTypes.func,
    groupRenderer: PropTypes.func,
    groupTitleRenderer: PropTypes.func,
    shallowUpdateCheck: PropTypes.bool,
    forceRedrawFunc: PropTypes.func,
    isLeftComponentPresent: PropTypes.bool
  };

  static defaultProps = {
    rowLayers: [],
    groupOffset: 150,
    itemHeight: 40,
    snapMinutes: 15,
    cursorTimeFormat: 'D MMM YYYY HH:mm',
    componentId: 'r9k1',
    showCursorTime: true,
    groupRenderer: DefaultGroupRenderer,
    itemRenderer: DefaultItemRenderer,
    groupTitleRenderer: () => <div />,
    timelineMode: Timeline.TIMELINE_MODES.SELECT | Timeline.TIMELINE_MODES.DRAG | Timeline.TIMELINE_MODES.RESIZE,
    shallowUpdateCheck: false,
    forceRedrawFunc: null,
    isLeftComponentPresent: false,
    onItemHover() { },
    onItemLeave() { }
  };

  /**
   * The types of interactions - see {@link onInteraction}
   */
  static changeTypes = {
    resizeStart: 'resizeStart',
    resizeEnd: 'resizeEnd',
    dragEnd: 'dragEnd',
    dragStart: 'dragStart',
    itemsSelected: 'itemsSelected',
    snappedMouseMove: 'snappedMouseMove'
  };

  /**
   * Checks if the given bit is set in the given mask
   * @param {number} bit Bit to check
   * @param {number} mask Mask to check against
   * @returns {boolean} True if bit is set; else false
   */
  static isBitSet(bit, mask) {
    return (bit & mask) === bit;
  }

  /**
   * Alias for no op function
   */
  static no_op = () => { };

  constructor(props) {
    super(props);
    this.selecting = false;
    this.state = {
      selection: [],
      cursorTime: null,
      gridWidth: 0,
      timelineWidth: 0,
      hoveringGroupResizer: false,
      draggingGroupResizer: false,
      leftComponentWidth: 0
    };
    this.setTimeMap(this.props.items);

    this.cellRenderer = this.cellRenderer.bind(this);
    this.rowHeight = this.rowHeight.bind(this);
    this.setTimeMap = this.setTimeMap.bind(this);
    this.getItem = this.getItem.bind(this);
    this.changeGroup = this.changeGroup.bind(this);
    this.setSelection = this.setSelection.bind(this);
    this.clearSelection = this.clearSelection.bind(this);
    this.getTimelineWidth = this.getTimelineWidth.bind(this);
    this.itemFromElement = this.itemFromElement.bind(this);
    this.updateDimensions = this.updateDimensions.bind(this);
    this.grid_ref_callback = this.grid_ref_callback.bind(this);
    this.select_ref_callback = this.select_ref_callback.bind(this);
    this.throttledMouseMoveFunc = _.throttle(this.throttledMouseMoveFunc.bind(this), 20);
    this.mouseMoveFunc = this.mouseMoveFunc.bind(this);
    this.getCursor = this.getCursor.bind(this);

    this.tableColumnWidthOverrides = [];
    this.debounceUpdateDimensions = _.debounce(this.updateDimensions, 100, {
      maxWait: 200,
      trailing: true
    }).bind(this);
 
    const canSelect = Timeline.isBitSet(Timeline.TIMELINE_MODES.SELECT, this.props.timelineMode);
    const canDrag = Timeline.isBitSet(Timeline.TIMELINE_MODES.DRAG, this.props.timelineMode);
    const canResize = Timeline.isBitSet(Timeline.TIMELINE_MODES.RESIZE, this.props.timelineMode);
    this.setUpDragging(canSelect, canDrag, canResize);
  }

  componentDidMount() {
    window.addEventListener('resize', this.updateDimensions);
  }

  componentWillReceiveProps(nextProps) {
    // Only rebuild the time map + grid when the items or the visible date range actually
    // change. This previously ran on every prop change (selection, callbacks, cursor,
    // unrelated parent state), rebuilding all row/overlap maps and recomputing the grid
    // needlessly. Stable memoized item references from the parent make this guard effective.
    if (this.props.items !== nextProps.items
      || this.props.startDate !== nextProps.startDate
      || this.props.endDate !== nextProps.endDate) {
      this.setTimeMap(nextProps.items, nextProps.startDate, nextProps.endDate);
      this.refreshGrid();
    }
  }

  componentWillUnmount() {
    if (this._itemInteractable) this._itemInteractable.unset();
    if (this._selectRectangleInteractable) this._selectRectangleInteractable.unset();

    window.removeEventListener('resize', this.updateDimensions);
  }

  componentDidUpdate(prevProps, prevState) {
    const { timelineMode, selectedItems } = this.props;
    const selectionChange = !_.isEqual(prevProps.selectedItems, selectedItems);
    const timelineModeChange = !_.isEqual(prevProps.timelineMode, timelineMode);
  
    if (timelineModeChange || selectionChange) {
      const canSelect = Timeline.isBitSet(Timeline.TIMELINE_MODES.SELECT, timelineMode);
      const canDrag = Timeline.isBitSet(Timeline.TIMELINE_MODES.DRAG, timelineMode);
      const canResize = Timeline.isBitSet(Timeline.TIMELINE_MODES.RESIZE, timelineMode);
      this.setUpDragging(canSelect, canDrag, canResize);
    }

    if (
      this.tableColumnWidthOverrides.length === 0
      || (this.props.tableColumns && (prevProps.tableColumns.length !== this.props.tableColumns.length))
      || (this.props.tableColumns && (this.props.tableColumns.length > 0 && this.props.tableColumns.length !== this.tableColumnWidthOverrides.length))
    ) {
      if (this.props.tableColumns) this.tableColumnWidthOverrides = this.props.tableColumns.map(x => Math.max(this.props.groupOffset, x.width));

      if (this.tableColumnWidthOverrides.length === 0) {
        this.tableColumnWidthOverrides.push(this.props.groupOffset);
      }

      this.debounceUpdateDimensions();
    }
  }

  /**
   * Re-renders the grid when the window or container is resized
   */
  updateDimensions() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.forceUpdate();
      if (this._grid) this._grid.recomputeGridSize();
    }, 100);
  }

  /**
   * Sets the internal maps used by the component for looking up item & row data
   * @param {Object[]} items The items to be displayed in the grid
   * @param {moment} startDate The visible start date of the timeline
   * @param {moment} endDate The visible end date of the timeline
   */
  setTimeMap(items, startDate, endDate) {
    perfCount('setTimeMap');
    if (!startDate || !endDate) {
      startDate = this.props.startDate;
      endDate = this.props.endDate;
    }
    this.itemRowMap = {}; // timeline elements (key) => (rowNo).
    this.rowItemMap = {}; // (rowNo) => timeline elements
    this.rowHeightCache = {}; // (rowNo) => max number of stacked items
    const visibleItems = _.filter(items, i => i.end > startDate && i.start < endDate);
    const itemRows = _.groupBy(visibleItems, 'row');
    _.forEach(itemRows, (visibleItems, row) => {
      const rowInt = parseInt(row);
      if (this.rowItemMap[rowInt] === undefined) this.rowItemMap[rowInt] = [];
      _.forEach(visibleItems, (item) => {
        this.itemRowMap[item.key] = rowInt;
        this.rowItemMap[rowInt].push(item);
      });
      this.rowHeightCache[rowInt] = getMaxOverlappingItems(visibleItems);
    });
  }

  /**
   * Returns an item given its DOM element
   * @param {Object} e the DOM element of the item
   * @return {Object} Item details
   * @prop {number|string} index The item's index
   * @prop {number} rowNo The row number the item is in
   * @prop {number} itemIndex Not really used - gets the index of the item in the row map
   * @prop {Object} item The provided item object
   */
  itemFromElement(e) {
    const index = e.getAttribute('data-item-index');
    const rowNo = this.itemRowMap[index];
    const itemIndex = _.findIndex(this.rowItemMap[rowNo], i => i.key == index);
    const item = this.rowItemMap[rowNo][itemIndex];

    return {
      index, rowNo, itemIndex, item
    };
  }

  /**
   * Gets an item given its ID
   * @param {number} id item id
   * @return {Object} Item object
   */
  getItem(id) {
    // This is quite stupid and shouldn't really be needed
    const rowNo = this.itemRowMap[id];
    const itemIndex = _.findIndex(this.rowItemMap[rowNo], i => i.key == id);
    return this.rowItemMap[rowNo][itemIndex];
  }

  /**
   * Move an item from one row to another
   * @param {object} item The item object whose groups is to be changed
   * @param {number} curRow The item's current row index
   * @param {number} newRow The item's new row index
   */
  changeGroup(item, curRow, newRow) {
    item.row = newRow;
    this.itemRowMap[item.key] = newRow;
    this.rowItemMap[curRow] = this.rowItemMap[curRow].filter(i => i.key !== item.key);
    this.rowItemMap[newRow].push(item);
  }

  /**
   * Set the currently selected time ranges (for the timebar to display)
   * @param {Object[]} selections Of the form `[[start, end], [start, end], ...]`
   */
  setSelection(selections) {
    const newSelection = _.map(selections, s => ({ start: s[0].clone(), end: s[1].clone() }));
    this.setState({ selection: newSelection });
  }

  /**
   * Clears the currently selected time range state
   */
  clearSelection() {
    this.setState({ selection: [] });
  }

  /**
   * Get the width of the timeline NOT including the left group list
   * @param {?number} totalWidth Total timeline width. If not supplied we use the timeline ref
   * @returns {number} The width in pixels
   */
  getTimelineWidth(totalWidth) {
    const groupOffset = this.calculateLeftOffset();
    if (totalWidth !== undefined) return totalWidth - groupOffset;
    return this._grid.props.width - groupOffset;
  }

  /**
   * re-computes the grid's row sizes
   * @param {Object?} config Config to pass wo react-virtualized's compute func
   */
  refreshGrid = (config = {}) => {
    try {
      this.setState(
        {
          ...this.state,
          gridWidth: config.width,
          timelineWidth: config.width - this.calculateLeftOffset(),
          leftComponentWidth: this.props.isLeftComponentPresent ? document.getElementById(this.props.leftComponentId).offsetWidth : 0
        },
        () => {
          this._grid.recomputeGridSize(config);      
        }
      );
    }
    catch (err) {
      console.warn(err);
    }
  };

  setUpDragging(canSelect, canDrag, canResize) {
    const topDivClassId = `rct9k-id-${this.props.componentId}`;
    const selectedItemSelector = '.rct9k-items-outer-selected';
    if (this._itemInteractable) this._itemInteractable.unset();
    if (this._selectRectangleInteractable) this._selectRectangleInteractable.unset();

    this._itemInteractable = interact(`.${topDivClassId} .item_draggable`);
    this._selectRectangleInteractable = interact(`.${topDivClassId} .parent-div`);

    this._itemInteractable.on('tap', (e) => {
      this._handleItemRowEvent(e, this.props.onItemClick, this.props.onRowClick);
    });
    
    if (canDrag) {
      this._itemInteractable
        .draggable({
          enabled: true,
          allowFrom: selectedItemSelector,
          restrict: {
            restriction: `.${topDivClassId}`,
            elementRect: {
              left: 0, right: 1, top: 0, bottom: 1
            }
          }
        })
        .on('dragstart', (e) => {
          const selections = [];
          const animatedItems = this.props.onInteraction(
            Timeline.changeTypes.dragStart,
            null,
            this.props.selectedItems
          );

          _.forEach(animatedItems, (id) => {
            const domItem = this._gridDomNode.querySelector(`span[data-item-index='${id}'`);
            if (domItem) {
              selections.push([this.getItem(id).start, this.getItem(id).end]);
              domItem.setAttribute('isDragging', 'True');
              domItem.setAttribute('drag-x', 0);
              domItem.setAttribute('drag-y', 0);
              domItem.style['z-index'] = 4;
            }
          });
          this.setSelection(selections);
        })
        .on('dragmove', (e) => {
          const { target } = e;
          const animatedItems = this._gridDomNode.querySelectorAll("span[isDragging='True'") || [];

          const dx = (parseFloat(target.getAttribute('drag-x')) || 0) + e.dx;
          const dy = (parseFloat(target.getAttribute('drag-y')) || 0) + e.dy;
          const selections = [];

          // Snap the movement to the current snap interval
          const snapDx = getSnapPixelFromDelta(
            dx,
            this.props.startDate,
            this.props.endDate,
            this.getTimelineWidth(),
            this.props.snapMinutes
          );

          _.forEach(animatedItems, (domItem) => {
            const { item } = this.itemFromElement(domItem);
            const itemDuration = item.end.diff(item.start);
            const newPixelOffset = pixToInt(domItem.style.left) + snapDx;
            const newStart = getTimeAtPixel(
              newPixelOffset,
              this.props.startDate,
              this.props.endDate,
              this.getTimelineWidth(),
              this.props.snapMinutes
            );

            const newEnd = newStart.clone().add(itemDuration);
            selections.push([newStart, newEnd]);

            // Translate the new start time back to pixels, so we can animate the snap
            domItem.style.webkitTransform = domItem.style.transform = `translate(${snapDx}px, ${dy}px)`;
          });

          target.setAttribute('drag-x', dx);
          target.setAttribute('drag-y', dy);

          this.setSelection(selections);
        })
        .on('dragend', (e) => {
          const { item, rowNo } = this.itemFromElement(e.target);
          const animatedItems = this._gridDomNode.querySelectorAll("span[isDragging='True'") || [];

          this.setSelection([[item.start, item.end]]);
          this.clearSelection();

          // Change row
          const newRow = getNearestRowNumber(e.clientX, e.clientY);

          const rowChangeDelta = newRow - rowNo;
          // Update time
          const newPixelOffset = pixToInt(e.target.style.left) + (parseFloat(e.target.getAttribute('drag-x')) || 0);
          const newStart = getTimeAtPixel(
            newPixelOffset,
            this.props.startDate,
            this.props.endDate,
            this.getTimelineWidth(),
            this.props.snapMinutes
          );

          const timeDelta = newStart.clone().diff(item.start, 'minutes');
          const changes = { rowChangeDelta, timeDelta };
          const items = [];

          // Default, all items move by the same offset during a drag
          _.forEach(animatedItems, (domItem) => {
            const { item, rowNo } = this.itemFromElement(domItem);

            const itemDuration = item.end.diff(item.start);
            const newStart = item.start.clone().add(timeDelta, 'minutes');
            const newEnd = newStart.clone().add(itemDuration);
            item.start = newStart;
            item.end = newEnd;
            if (rowChangeDelta < 0) {
              item.row = Math.max(0, item.row + rowChangeDelta);
            }
            else if (rowChangeDelta > 0) {
              item.row = Math.min(this.props.groups.length - 1, item.row + rowChangeDelta);
            }

            items.push(item);
          });

          this.props.onInteraction(Timeline.changeTypes.dragEnd, changes, items);

          // Reset the styles
          animatedItems.forEach((domItem) => {
            domItem.style.webkitTransform = domItem.style.transform = 'translate(0px, 0px)';
            domItem.setAttribute('drag-x', 0);
            domItem.setAttribute('drag-y', 0);
            domItem.style['z-index'] = 3;
            domItem.style.top = intToPix(
              this.props.itemHeight * Math.round(pixToInt(domItem.style.top) / this.props.itemHeight)
            );
            domItem.removeAttribute('isDragging');
          });

          this._grid.recomputeGridSize({ rowIndex: 0 });
        });
    }
    if (canResize) {
      this._itemInteractable
        .resizable({
          allowFrom: selectedItemSelector,
          edges: {
            left: true, right: true, bottom: false, top: false
          }
        })
        .on('resizestart', (e) => {
          const selected = this.props.onInteraction(Timeline.changeTypes.resizeStart, null, this.props.selectedItems);
          _.forEach(selected, (id) => {
            const domItem = this._gridDomNode.querySelector(`span[data-item-index='${id}'`);
            if (domItem) {
              domItem.setAttribute('isResizing', 'True');
              domItem.setAttribute('initialWidth', pixToInt(domItem.style.width));
              domItem.style['z-index'] = 4;
            }
          });
        })
        .on('resizemove', (e) => {
          const animatedItems = this._gridDomNode.querySelectorAll("span[isResizing='True'") || [];

          let dx = parseFloat(e.target.getAttribute('delta-x')) || 0;
          dx += e.deltaRect.left;

          const dw = e.rect.width - Number(e.target.getAttribute('initialWidth'));

          const minimumWidth = pixelsPerMinute(this.props.startDate, this.props.endDate, this.getTimelineWidth()) * this.props.snapMinutes;

          const snappedDx = getSnapPixelFromDelta(
            dx,
            this.props.startDate,
            this.props.endDate,
            this.getTimelineWidth(),
            this.props.snapMinutes
          );

          const snappedDw = getSnapPixelFromDelta(
            dw,
            this.props.startDate,
            this.props.endDate,
            this.getTimelineWidth(),
            this.props.snapMinutes
          );

          _.forEach(animatedItems, (item) => {
            item.style.width = intToPix(Number(item.getAttribute('initialWidth')) + snappedDw + minimumWidth);
            item.style.webkitTransform = item.style.transform = `translate(${snappedDx}px, 0px)`;
          });
          e.target.setAttribute('delta-x', dx);
        })
        .on('resizeend', (e) => {
          const animatedItems = this._gridDomNode.querySelectorAll("span[isResizing='True'") || [];
          // Update time
          const dx = parseFloat(e.target.getAttribute('delta-x')) || 0;
          const isStartTimeChange = dx != 0;

          const items = [];
          let minRowNo = Infinity;

          let durationChange = null;
          // Calculate the default item positions
          _.forEach(animatedItems, (domItem) => {
            const startPixelOffset = pixToInt(domItem.style.left) + dx;
            const { item, rowNo } = this.itemFromElement(domItem);

            minRowNo = Math.min(minRowNo, rowNo);

            if (isStartTimeChange) {
              const newStart = getTimeAtPixel(
                startPixelOffset,
                this.props.startDate,
                this.props.endDate,
                this.getTimelineWidth(),
                this.props.snapMinutes
              );
              if (durationChange === null) durationChange = item.start.diff(newStart, 'minutes');
              item.start = newStart;
            }
            else {
              const endPixelOffset = startPixelOffset + pixToInt(domItem.style.width);
              const newEnd = getTimeAtPixel(
                endPixelOffset,
                this.props.startDate,
                this.props.endDate,
                this.getTimelineWidth(),
                this.props.snapMinutes
              );
              if (durationChange === null) durationChange = item.end.diff(newEnd, 'minutes');

              item.end = newEnd;
            }

            // Check row height doesn't need changing
            const new_row_height = getMaxOverlappingItems(
              this.rowItemMap[rowNo],
              this.props.startDate,
              this.props.endDate
            );
            if (new_row_height !== this.rowHeightCache[rowNo]) {
              this.rowHeightCache[rowNo] = new_row_height;
            }

            // Reset styles
            domItem.removeAttribute('isResizing');
            domItem.removeAttribute('initialWidth');
            domItem.style['z-index'] = 3;
            domItem.style.webkitTransform = domItem.style.transform = 'translate(0px, 0px)';

            items.push(item);
          });
          if (durationChange === null) durationChange = 0;
          const changes = { isStartTimeChange, timeDelta: -durationChange };

          this.props.onInteraction(Timeline.changeTypes.resizeEnd, changes, items);

          e.target.setAttribute('delta-x', 0);
          this._grid.recomputeGridSize({ rowIndex: minRowNo });
        });
    }

    if (canSelect) {
      this._selectRectangleInteractable
        .draggable({
          enabled: true,
          ignoreFrom: '.item_draggable, .rct9k-group'
        })
        .styleCursor(false)
        .on('dragstart', (e) => {
          const nearestRowObject = getNearestRowObject(e.clientX, e.clientY);

          // this._selectBox.start(e.clientX, e.clientY);
          // this._selectBox.start(e.clientX, topRowObj.style.top);
          this._selectBox.start(e.clientX, nearestRowObject.getBoundingClientRect().y);
          // const bottomRow = Number(getNearestRowNumber(left + width, top + height));
        })
        .on('dragmove', (e) => {
          const magicalConstant = 2;
          // @bendog: I added this magical constant to solve the issue of selection bleed,
          // I don't understand why it works, but if frequentist statisticians can use imaginary numbers, so can i.
          const { startX, startY } = this._selectBox;
          const startRowObject = getNearestRowObject(startX, startY);
          const { clientX, clientY } = e;
          const currentRowObject = getNearestRowObject(clientX, clientY);
          if (currentRowObject !== undefined && startRowObject !== undefined) {
            // only run if you can detect the top row
            const startRowNumber = getRowObjectRowNumber(startRowObject);
            const currentRowNumber = getRowObjectRowNumber(currentRowObject);
            // const numRows = 1 + Math.abs(startRowNumber - currentRowNumber);
            const rowMarginBorder = getVerticalMarginBorder(currentRowObject);
            if (startRowNumber <= currentRowNumber) {
              // select box for selection going down
              // get the first selected rows top
              const startTop = Math.ceil(startRowObject.getBoundingClientRect().top + rowMarginBorder);
              // get the currently selected rows bottom
              const currentBottom = Math.floor(getTrueBottom(currentRowObject) - magicalConstant - rowMarginBorder);
              this._selectBox.start(startX, startTop);
              this._selectBox.move(clientX, currentBottom);
            }
            else {
              // select box for selection going up
              // get the currently selected rows top
              const currentTop = Math.ceil(currentRowObject.getBoundingClientRect().top + rowMarginBorder);
              // get the first selected rows bottom
              const startBottom = Math.floor(getTrueBottom(startRowObject) - magicalConstant - rowMarginBorder * 2);
              // the bottom will bleed south unless you counter the margins and boreders from the above rows
              this._selectBox.start(startX, startBottom);
              this._selectBox.move(clientX, currentTop);
            }
          }
        })
        .on('dragend', (e) => {
          let {
            top, left, width, height
          } = this._selectBox.end();
          // Get the start and end row of the selection rectangle
          const topRowObject = getNearestRowObject(left, top);
          if (topRowObject !== undefined) {
            // only confirm the end of a drag if the selection box is valid
            const topRowNumber = Number(getNearestRowNumber(left, top));
            const topRowLoc = topRowObject.getBoundingClientRect();
            const rowMarginBorder = getVerticalMarginBorder(topRowObject);
            const bottomRow = Number(
              getNearestRowNumber(
                left + width,
                Math.floor(topRowLoc.top - rowMarginBorder) + Math.floor(height - rowMarginBorder)
              )
            );
            // Get the start and end time of the selection rectangle
            left -= this.props.groupOffset;
            const startOffset = width > 0 ? (left - this.state.leftComponentWidth) : (left - this.state.leftComponentWidth) + width;
            const endOffset = width > 0 ? (left - this.state.leftComponentWidth) + width : (left - this.state.leftComponentWidth);
            const startTime = getTimeAtPixel(
              startOffset,
              this.props.startDate,
              this.props.endDate,
              this.getTimelineWidth(),
              this.props.snapMinutes
            );
            const endTime = getTimeAtPixel(
              endOffset,
              this.props.startDate,
              this.props.endDate,
              this.getTimelineWidth(),
              this.props.snapMinutes
            );
            // Get items in these ranges
            const selectedItems = [];
            for (let r = Math.min(topRowNumber, bottomRow); r <= Math.max(topRowNumber, bottomRow); r++) {
              selectedItems.push(
                ..._.filter(this.rowItemMap[r], i => i.start.isBefore(endTime) && i.end.isAfter(startTime))
              );
            }
            this.props.onInteraction(Timeline.changeTypes.itemsSelected, selectedItems);
          }
        });
    }
  }

  _handleItemRowEvent = (e, itemCallback, rowCallback) => {
    e.preventDefault();
    // Skip click handler if selecting with selection box
    if (this.selecting) {
      return;
    }
    if (e.target.hasAttribute('data-item-index') || e.target.parentElement.hasAttribute('data-item-index')) {
      const itemKey = e.target.getAttribute('data-item-index') || e.target.parentElement.getAttribute('data-item-index');
      itemCallback && itemCallback(e, Number(itemKey));
    }
    else {
      const row = e.target.getAttribute('data-row-index');
      const leftComponentOffsetWidth = this.props.isLeftComponentPresent ? this.state.leftComponentWidth : 0;
      const clickedTime = getTimeAtPixel(
        e.clientX - leftComponentOffsetWidth - this.calculateLeftOffset(), // -311
        this.props.startDate,
        this.props.endDate,
        this.getTimelineWidth()
      );

      // const roundedStartMinutes = Math.round(clickedTime.minute() / this.props.snapMinutes) * this.props.snapMinutes; // I dont know what this does
      const snappedClickedTime = timeSnap(clickedTime, this.props.snapMinutes * 60);
      rowCallback && rowCallback(e, row, clickedTime, snappedClickedTime);
    }
  };

  _handleRowMouseMove = (e) => {
    const { onRowMouseMove, isLeftComponentPresent, startDate, endDate, snapMinutes } = this.props;
    if (!onRowMouseMove) {
      return;
    }
    if (e.target.hasAttribute('data-item-index') || e.target.parentElement.hasAttribute('data-item-index')) {
      return;
    }
    const row = e.target.getAttribute('data-row-index');
    if (row == null) {
      return;
    }
    const leftComponentOffsetWidth = isLeftComponentPresent ? this.state.leftComponentWidth : 0;
    const clickedTime = getTimeAtPixel(
      e.clientX - leftComponentOffsetWidth - this.calculateLeftOffset(),
      startDate,
      endDate,
      this.getTimelineWidth()
    );
    const snappedClickedTime = timeSnap(clickedTime, snapMinutes * 60);
    onRowMouseMove(row, snappedClickedTime);
  };

  updateGroupResizerState(hovering, dragging) {
    this.setState({
      ...this.state,
      hoveringGroupResizer: hovering ?? this.state.hoveringGroupResizer,
      draggingGroupResizer: dragging ?? this.state.draggingGroupResizer,
    });
    this.updateDimensions();
  }

  cellRenderer() {
    /**
     * @param  {} columnIndex Always 1
     * @param  {} key Unique key within array of cells
     * @param  {} parent Reference to the parent Grid (instance)
     * @param  {} rowIndex Vertical (row) index of cell
     * @param  {} style Style object to be applied to cell (to position it);
     */

    // Note: don't do anything with props here, outside of the arrow function that gets returned,
    // since this closure won't get updated in the way you might expect

    // This arrow function gets passed to TimelineBody, which passes it to Grid, and while you will
    // get a new instance of this arrow function (and thus a new closure) every time Timeline
    // re-renders, TimelineBody doesn't always re-render, and thus the new arrow function doesn't
    // always get passed down to Grid, so you'll still be using the old closure

    // eslint-disable-next-line
    return ({
      columnIndex, key, parent, rowIndex, style
    }) => {
      const {
        timelineMode,
        onItemHover,
        onItemLeave,
        rowLayers,
        groupOffset,
        itemHeight,
        onRowClick,
        onItemContextClick,
        onRowContextClick,
        onRowMouseMove,
        onItemDoubleClick,
        onRowDoubleClick,
        startDate,
        endDate,
        itemRenderer,
        selectedItems,
        groups,
        groupRenderer,
      } = this.props;
      const canSelect = Timeline.isBitSet(Timeline.TIMELINE_MODES.SELECT, timelineMode);
      // eslint-disable-next-line
      const width = this._grid.props.width - this.calculateLeftOffset();
      const itemCol = 1;
      if (itemCol === columnIndex) {
        const itemsInRow = this.rowItemMap[rowIndex];
        const layersInRow = rowLayers.filter(r => r.rowNumber === rowIndex);
        let rowHeight = itemHeight;
        if (this.rowHeightCache[rowIndex]) {
          rowHeight *= this.rowHeightCache[rowIndex];
        }
        return (
          <div
            key={key}
            style={style}
            data-row-index={rowIndex}
            className="rct9k-row"
            // eslint-disable-next-line
            onClick={e => this._handleItemRowEvent(e, Timeline.no_op, onRowClick)}
            onMouseDown={() => {
              this.selecting = false;
            }}
            onMouseMove={(e) => {
              this.selecting = true;
              this._handleRowMouseMove(e);
            }}
            onFocus={(e) => {
              this.selecting = false;
              // eslint-disable-next-line
              return this._handleItemRowEvent(e, onItemHover, null);
            }}
            onMouseOver={(e) => {
              this.selecting = false;
              // eslint-disable-next-line
              return this._handleItemRowEvent(e, onItemHover, null);
            }}
            onMouseLeave={(e) => {
              this.selecting = false;
              // eslint-disable-next-line
              return this._handleItemRowEvent(e, onItemLeave, null);
            }}
            // eslint-disable-next-line
            onContextMenu={e => this._handleItemRowEvent(e, onItemContextClick, onRowContextClick)
            }
            // eslint-disable-next-line
            onDoubleClick={e => this._handleItemRowEvent(e, onItemDoubleClick, onRowDoubleClick)}
          >
            {rowItemsRenderer(
              itemsInRow,
              startDate,
              endDate,
              width,
              itemHeight,
              itemRenderer,
              canSelect ? selectedItems : []
            )}
            {rowLayerRenderer(layersInRow, startDate, endDate, width, rowHeight)}
          </div>
        );
      }
      const GroupComp = groupRenderer;
      const group = _.find(groups, g => g.id === rowIndex);
      return (
        <div
          data-row-index={rowIndex}
          key={key}
          style={{
            ...style,
            minWidth: style.width
          }}
          className="rct9k-group"
        >
          <GroupComp
            group={group}
            timelineExtras={(
              <DraggableCore
                axis="x"
                handle={'.rct9k-group__resizeHandle'}
                onDrag={(e, data) => this.handleColumnDrag(e, data, columnIndex)}
                allowAnyClick={false}
                onStart={() => {
                  this.updateGroupResizerState(undefined, true);
                }}
                onStop={() => {
                  this.updateGroupResizerState(undefined, false);
                }}
              >

                <div
                  className={
                    [
                      "rct9k-group__resizeHandle",
                      (this.state.hoveringGroupResizer || this.state.draggingGroupResizer) ? "rct9k-group__resizeHandle--hover" : ""
                    ].join(" ")
                  }
                  onMouseEnter={() => {
                    this.updateGroupResizerState(true);
                  }}
                  onMouseLeave={() => {
                    this.updateGroupResizerState(false);
                  }}
                />

              </DraggableCore>
            )}
          />
        </div>
      );
    };
  }

  calculateLeftOffset() {
    /* eslint-disable */
    const {
      tableColumns,
      groupOffset,
    } = this.props;

    if (!!tableColumns && tableColumns.length > 0) {
      return (
        tableColumns
          .map((c, i) => this.tableColumnWidthOverrides[i] || c.width || groupOffset)
          .reduce((a, b) => a + b, 0) || groupOffset
      ) + 4;
    }

    if (this.tableColumnWidthOverrides.length > 0) {
      return (this.tableColumnWidthOverrides.reduce((a, b) => a + b, 0) || groupOffset) + 4;
    }
    
    return groupOffset + 4;
    /* eslint-enable */
  }

  handleColumnDrag(e, data, columnIndex) {
    const {
      groupOffset,
    } = this.props;

    if (data.deltaX === 0 && data.deltaY !== 0) {
      return;
    }

    this.tableColumnWidthOverrides[columnIndex] = Math.max(
      groupOffset,
      Math.min(groupOffset * 4, (this.tableColumnWidthOverrides[columnIndex] || 0) + data.deltaX)
    );

    this.debounceUpdateDimensions();
  }

  getCursor() {
    const { showCursorTime, cursorTimeFormat } = this.props;
    const { cursorTime } = this.state;
    return showCursorTime && cursorTime ? cursorTime.clone().format(cursorTimeFormat) : null;
  }

  /**
   * Helper for react virtuaized to get the row height given a row index
   */
  rowHeight({ index }) {
    const {
      itemHeight,
    } = this.props;

    const rh = this.rowHeightCache[index] ? this.rowHeightCache[index] : 1;
    return rh * itemHeight;
  }

  /**
   * Set the grid ref.
   * @param {Object} reactComponent Grid react element
   */
  grid_ref_callback(reactComponent) {
    // eslint-disable-next-line
    this._grid = reactComponent;
    // eslint-disable-next-line
    this._gridDomNode = ReactDOM.findDOMNode(this._grid);
  }

  /**
   * Set the select box ref.
   * @param {Object} reactComponent Selectbox react element
   */
  select_ref_callback(reactComponent) {
    // eslint-disable-next-line
    this._selectBox = reactComponent;
  }

  /**
   * Event handler for onMouseMove.
   * Only calls back if a new snap time is reached
   */
  throttledMouseMoveFunc(e) {
    const {
      startDate,
      endDate,
      snapMinutes,
      onInteraction,
      isLeftComponentPresent
    } = this.props;
    const { leftComponentWidth } = this.state;
    const leftComponentOffsetWidth = isLeftComponentPresent ? leftComponentWidth : 0;
    const cursorSnappedTime = getTimeAtPixel(
      (e.clientX - leftComponentOffsetWidth) - this.calculateLeftOffset(),
      startDate,
      endDate,
      this.getTimelineWidth(),
      snapMinutes
    );
    if (!this.mouse_snapped_time || this.mouse_snapped_time.unix() !== cursorSnappedTime.unix()) {
      if (cursorSnappedTime.isSameOrAfter(startDate)) {
        this.mouse_snapped_time = cursorSnappedTime;
        this.setState({ cursorTime: this.mouse_snapped_time });
        onInteraction(
          Timeline.changeTypes.snappedMouseMove,
          { snappedTime: this.mouse_snapped_time.clone() },
          null
        );
      }
    }
  }

  mouseMoveFunc(e) {
    e.persist();
    this.throttledMouseMoveFunc(e);
  }

  render() {
    const {
      showCursorTime,
      timebarFormat,
      componentId,
      groupTitleRenderer,
      shallowUpdateCheck,
      forceRedrawFunc,
      startDate,
      endDate,
      groups,
    } = this.props;

    const {
      selection,
    } = this.state;

    const divCssClass = `rct9k-timeline-div rct9k-id-${componentId}`;
    const varTimebarProps = {};
    if (timebarFormat) varTimebarProps.timeFormats = timebarFormat;

    const columnWidth = width => ({ index }) => {
      if (index === 0) return this.calculateLeftOffset();
      return width - this.calculateLeftOffset();
    };
    // Markers (only current time marker atm)
    const markers = [];
    if (showCursorTime && this.mouse_snapped_time) {
      const cursorPix = getPixelAtTime(this.mouse_snapped_time, startDate, endDate, this.getTimelineWidth());
      markers.push({
        left: cursorPix + this.calculateLeftOffset(),
        key: 1
      });
    }
    return (
      <div className={divCssClass}>
        <AutoSizer className="rct9k-autosizer" onResize={this.refreshGrid}>
          {({ height, width }) => (
            <div className="parent-div" onMouseMove={this.mouseMoveFunc}>
              <SelectBox ref={this.select_ref_callback} />
              <Timebar
                cursorTime={this.getCursor()}
                start={startDate}
                end={endDate}
                width={width}
                leftOffset={this.calculateLeftOffset()}
                selectedRanges={selection}
                groupTitleRenderer={groupTitleRenderer}
                {...varTimebarProps}
              />
              {markers.map(m => (
                <Marker key={m.key} height={height} top={0} left={m.left} />
              ))}
              <TimelineBody
                width={width}
                columnWidth={columnWidth(width)}
                height={height}
                rowHeight={this.rowHeight}
                rowCount={groups.length}
                cellRenderer={this.cellRenderer()}
                grid_ref_callback={this.grid_ref_callback}
                shallowUpdateCheck={shallowUpdateCheck}
                forceRedrawFunc={forceRedrawFunc}
              />
            </div>
          )}
        </AutoSizer>
      </div>
    );
  }
}
